import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";

import propTypes from "prop-types";
import cn from "classnames";
import Icon from "../icons/Icon";
import Button from "../button/Button";

import "./Modal.scss";
import { CSSTransition } from "react-transition-group";

import { guid, createDataAttributes } from "../../utilities";

const Modal = ({
  id = guid(),
  show,
  title,
  subTitle,
  body,
  footer,
  onHide,
  onReveal,
  closeIcon,
  overlayHidden,
  size,
  className,
  containerClassName,
  altCloseMethod,
  dataWrapperAttrib,
  dataModalHeaderAttrib,
  dataModalBodyAttrib,
  dataModalFooterAttrib,
  ...rest
}) => {
  /**
   * don't set state value from "show" by default... let the useEffect
   * update it so the transition occurs in an expected way
   */
  const [showModal, setShowModal] = useState();

  /**
   * always make sure the showModal is applied by the prop at any time it changes
   */
  useEffect(() => setShowModal(show), [show]);

  const overlayRef = useRef();
  const modalRef = useRef();
  const prevShow = useRef();

  const onClose = useCallback(() => {
    if (setShowModal) setShowModal(false);
    if (onHide) onHide(false); // return value in callback
  }, [onHide]);

  /**
   * a useEffect to trigger the onHide if the `show` prop is
   * what causes the modal visibilty to change (instead of a user click)
   */
  useEffect(() => {
    if (showModal && !show && prevShow.current && onHide) {
      onHide(false);
    }
    prevShow.current = show;
  }, [onHide, show, showModal]);

  // handle clicking outside of the menu to close the menu
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    const escKey = event => {
      const e = event || window.event;
      if (e.key.toLowerCase() === "escape") {
        e.preventDefault();
        onClose();
      }
    };

    /**
     * when modal is shown, if we allow closing modal using ESC key or clicking background
     * then we attach event listeners to close the modal
     */
    if (showModal && altCloseMethod && !overlayHidden) {
      document.addEventListener("keydown", escKey, true);
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
        document.removeEventListener("keydown", escKey, true);
      };
    }
  }, [showModal, altCloseMethod, overlayHidden, modalRef, onClose]);

  /**
   * this useEffect is necessary for scenarios where the component is conditional
   * because the component render can be controlled by a condition like:
   * {show && <Modal show/>} instead of just <Modal show/>
   * it's only purpose is to make sure the class `uikit__modal-open` is bound to the <body> tag
   * whe the modal is open and remove when it's closed
   */
  useEffect(() => {
    if (showModal) document.body.classList.add(`uikit__modal-open-${id}`);
    else document.body.classList.remove(`uikit__modal-open-${id}`);
    return () => {
      document.body.classList.remove(`uikit__modal-open-${id}`);
    };
  }, [showModal, id]);

  return (
    <CSSTransition
      classNames="uikit__modal__transition"
      in={showModal}
      timeout={300}
      unmountOnExit
      nodeRef={overlayRef}
      /**
       * after modal is opened... tag it with a focus so the Tab key can
       * navigate to the content of the modal, instead of tabbing in the background
       */
      onEntered={() => {
        document.getElementById(`uikit__modal__overlay__${id}`).focus();
        if (onReveal) onReveal();
      }}
    >
      <>
        {ReactDOM.createPortal(
          <div
            id={`uikit__modal__overlay__${id}`}
            ref={overlayRef}
            className={cn("uikit__modal__overlay", "ufg-modal-backdrop", {
              "uikit__modal__overlay-hidden": overlayHidden,
              [`ufg-modal-backdrop-${containerClassName}`]: containerClassName
            })}
            tabIndex="-1"
            {...createDataAttributes(dataWrapperAttrib)}
            {...rest}
          >
            <div
              id={`uikit__modal__dialog__${id}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`uikit__modal__title__${id}`}
              ref={modalRef}
              className={cn(
                className,
                "uikit__modal",
                "modal-dialog",
                "uikit__modal__container",
                `uikit__modal__size-${size}`,
                {
                  [`ufg-modal-container-${containerClassName}`]:
                    containerClassName,
                  show
                },
                "ufg-modal"
              )}
            >
              <div className="uikit__modal__content ufg-modal-content">
                {(title || subTitle) && (
                  <div
                    className="uikit__modal__header ufg-modal-header"
                    {...createDataAttributes(dataModalHeaderAttrib)}
                  >
                    <div className="uikit__modal__header-inner">
                      {title && (
                        <div
                          id={`uikit__modal__title__${id}`}
                          className="uikit__modal__title ufg-modal-title "
                        >
                          {title}
                        </div>
                      )}

                      {subTitle && (
                        <div className="uikit__modal__header-text">
                          {subTitle}
                        </div>
                      )}
                    </div>
                    {closeIcon && (
                      <Button
                        type="button"
                        aria-label="close"
                        className="uikit__modal__close uikit__modal__close-title"
                        onClick={() => onClose()}
                      >
                        <Icon icon="farTimes" />
                      </Button>
                    )}
                  </div>
                )}
                {!title && !subTitle && closeIcon && (
                  <div className="uikit__modal__closeX">
                    <Button
                      type="button"
                      aria-label="close"
                      className="uikit__modal__close uikit__modal__close-notitle"
                      onClick={() => onClose()}
                    >
                      <Icon icon="farTimes" />
                    </Button>
                  </div>
                )}
                <div
                  id={`uikit__modal__body__${id}`}
                  className="uikit__modal__body ufg-modal-body"
                  {...createDataAttributes(dataModalBodyAttrib)}
                >
                  {body}
                </div>
                {footer && (
                  <div
                    className="uikit__modal__footer ufg-modal-footer"
                    {...createDataAttributes(dataModalFooterAttrib)}
                  >
                    {footer}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    </CSSTransition>
  );
};

Modal.propTypes = {
  id: propTypes.string,
  show: propTypes.bool.isRequired,
  title: propTypes.oneOfType([propTypes.element, propTypes.string]),
  subTitle: propTypes.oneOfType([
    propTypes.element,
    propTypes.string,
    propTypes.node,
    propTypes.func,
    propTypes.array
  ]),
  body: propTypes.oneOfType([
    propTypes.element,
    propTypes.string,
    propTypes.node,
    propTypes.func,
    propTypes.array
  ]).isRequired,
  containerClassName: propTypes.string,
  closeIcon: propTypes.bool,
  footer: propTypes.oneOfType([propTypes.element, propTypes.string]),
  onHide: propTypes.func,
  onReveal: propTypes.func,
  size: propTypes.oneOf(["sm", "md", "lg", "xl", "xxl"]),
  className: propTypes.string,
  overlayHidden: propTypes.bool,
  altCloseMethod: propTypes.bool,
  dataWrapperAttrib: propTypes.arrayOf(Object),
  dataModalHeaderAttrib: propTypes.arrayOf(Object),
  dataModalBodyAttrib: propTypes.arrayOf(Object),
  dataModalFooterAttrib: propTypes.arrayOf(Object)
};
Modal.defaultProps = {
  closeIcon: true,
  onHide: () => {},
  size: "md",
  overlayHidden: false,
  altCloseMethod: true
};

export default Modal;
